import { Button, Input } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { config } from "@/shared/lib/config";

type ClassTypeItem = { id: string; name: string };
type LevelItem = { id: string; name: string };

export function ClassTemplateFilter() {
  const nav = useNavigate({ from: "/app/classes/templates" });
  const search = useSearch({
    from: "/app/classes/templates",
  }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const classTypeFilter = findCondition(filters, "classTypeId");
  const levelFilter = findCondition(filters, "levelId");
  const statusFilter = findCondition(filters, "status");
  const durationMinFilter = filters.find(
    (f) => f.field === "duration" && f.op === "gte",
  );
  const durationMaxFilter = filters.find(
    (f) => f.field === "duration" && f.op === "lte",
  );

  const [classTypeId, setClassTypeId] = useState<string>(
    (classTypeFilter?.value as string) || "",
  );
  const [levelId, setLevelId] = useState<string>(
    (levelFilter?.value as string) || "",
  );
  const [durationMin, setDurationMin] = useState<string>(
    (durationMinFilter?.value &&
    typeof durationMinFilter.value === "number"
      ? String(durationMinFilter.value)
      : "") || "",
  );
  const [durationMax, setDurationMax] = useState<string>(
    (durationMaxFilter?.value &&
    typeof durationMaxFilter.value === "number"
      ? String(durationMaxFilter.value)
      : "") || "",
  );

  useEffect(
    () => setClassTypeId((classTypeFilter?.value as string) || ""),
    [classTypeFilter?.value],
  );
  useEffect(
    () => setLevelId((levelFilter?.value as string) || ""),
    [levelFilter?.value],
  );
  useEffect(() => {
    setDurationMin(
      durationMinFilter?.value && typeof durationMinFilter.value === "number"
        ? String(durationMinFilter.value)
        : "",
    );
  }, [durationMinFilter?.value]);
  useEffect(() => {
    setDurationMax(
      durationMaxFilter?.value && typeof durationMaxFilter.value === "number"
        ? String(durationMaxFilter.value)
        : "",
    );
  }, [durationMaxFilter?.value]);

  const statusValue =
    typeof statusFilter?.value === "string" ? statusFilter.value : "";
  const hasFilter = !!classTypeId || !!levelId || !!statusValue || !!durationMin || !!durationMax;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <InfiniteCombobox<ClassTypeItem>
            queryKey={["master", "class-types", "lookup"]}
            queryFn={({ search, pageParam }) =>
              fetchLookupForInfinite<ClassTypeItem>(
                `${config.apiUrl}/master/class-types/lookup`,
                { search, pageParam },
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<ClassTypeItem>(
                `${config.apiUrl}/master/class-types/lookup`,
                id,
              ).then((item) => item ?? null)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={classTypeId}
            onValueChange={(val) => {
              setClassTypeId(val);
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "classTypeId",
                  op: "eq",
                  value: val,
                });
              } else {
                nextFilters = removeConditions(filters, "classTypeId");
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ປະເພດຄລາດສ໌"
            className="h-8 w-full sm:w-48"
            clearable
          />

          <InfiniteCombobox<LevelItem>
            queryKey={["master", "levels", "lookup"]}
            queryFn={({ search, pageParam }) =>
              fetchLookupForInfinite<LevelItem>(
                `${config.apiUrl}/master/levels/lookup`,
                { search, pageParam },
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<LevelItem>(
                `${config.apiUrl}/master/levels/lookup`,
                id,
              ).then((item) => item ?? null)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={levelId}
            onValueChange={(val) => {
              setLevelId(val);
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "levelId",
                  op: "eq",
                  value: val,
                });
              } else {
                nextFilters = removeConditions(filters, "levelId");
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ລະດັບ"
            className="h-8 w-full sm:w-48"
            clearable
          />

          <Input
            type="number"
            placeholder="ລາຍການຕ່ຳສຸດ (ນາທີ)"
            value={durationMin}
            onChange={(e) => {
              const val = e.target.value;
              setDurationMin(val);
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "duration",
                  op: "gte",
                  value: Number.parseInt(val, 10),
                });
              } else {
                // Remove gte filter only, keep lte if exists
                nextFilters = filters.filter(
                  (f) => !(f.field === "duration" && f.op === "gte"),
                );
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            className="h-8 w-full sm:w-32"
          />

          <Input
            type="number"
            placeholder="ລາຍການສູງສຸດ (ນາທີ)"
            value={durationMax}
            onChange={(e) => {
              const val = e.target.value;
              setDurationMax(val);
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                // Remove existing lte filter if any
                nextFilters = filters.filter(
                  (f) => !(f.field === "duration" && f.op === "lte"),
                );
                nextFilters = upsertCondition(nextFilters, {
                  field: "duration",
                  op: "lte",
                  value: Number.parseInt(val, 10),
                });
              } else {
                nextFilters = filters.filter(
                  (f) => !(f.field === "duration" && f.op === "lte"),
                );
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            className="h-8 w-full sm:w-32"
          />

          <SimpleSelect
            value={statusValue || "all"}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-32"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "active", label: "ໃຊ້ງານ" },
              { value: "inactive", label: "ບໍ່ໃຊ້ງານ" },
            ]}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({ search: { ...search, filters: undefined, offset: 0 } })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
